import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import type { ToastRecord } from './toast.model';
import { clientLogger } from '@mb/common/client';

@Component({
  selector: 'app-toast-item',
  standalone: true,
  imports: [CommonModule, NgComponentOutlet],
  template: `
    @if (toast.content.kind === 'text') {
      <div
        style="pointer-events: auto;"
        class="pointer-events-auto w-full max-w-[min(480px,calc(100vw-2rem))] rounded-lg border bg-background text-foreground shadow-xl"
        [class]="variantClass"
        role="status"
        aria-live="polite"
      >
        <div class="flex gap-3 p-4">
          <div class="flex-1 min-w-0">
            <div class="font-medium leading-5 break-words">
              {{ toast.content.title }}
            </div>
            @if (toast.content.description) {
              <div class="mt-1 text-sm opacity-90 break-words">
                {{ toast.content.description }}
              </div>
            }

            @if (toast.action || toast.cancel) {
              <div class="mt-3 flex gap-2 flex-wrap">
                @if (toast.action) {
                  <button
                    type="button"
                    [disabled]="isLoading() || toast.action.isDisabled"
                    class="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    (click)="onActionClick($event)"
                  >
                    @if (isLoading()) {
                      <svg
                        class="w-3.5 h-3.5 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          class="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="4"
                        ></circle>
                        <path
                          class="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    }
                    {{ toast.action.label }}
                  </button>
                }
                @if (toast.cancel) {
                  <button
                    type="button"
                    [disabled]="isLoading()"
                    class="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium bg-secondary text-secondary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    (click)="onCancelClick($event)"
                  >
                    {{ toast.cancel.label }}
                  </button>
                }
              </div>
            }
          </div>

          @if (toast.dismissible) {
            <button
              type="button"
              class="shrink-0 rounded-md p-2 hover:bg-muted"
              aria-label="Close"
              (click)="dismiss.emit(toast.id)"
            >
              <span class="sr-only">Close</span>
              <svg
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          }
        </div>
      </div>
    } @else {
      <!-- Custom component toasts (e.g. bottom toast) render their own surface.
           Don't wrap them with the default bordered/background card. -->
      <div style="pointer-events: auto;" class="pointer-events-auto">
        <ng-container
          *ngComponentOutlet="
            toast.content.component;
            inputs: toast.content.componentInputs
          "
        />
      </div>
    }
  `,
})
export class ToastItemComponent {
  @Input({ required: true }) toast!: ToastRecord;

  @Output() dismiss = new EventEmitter<string>();
  @Output() action = new EventEmitter<MouseEvent>();
  @Output() cancelClick = new EventEmitter<MouseEvent>();

  private readonly _isLoading = signal(false);
  public readonly isLoading = this._isLoading.asReadonly();

  public get variantClass(): string {
    switch (this.toast.variant) {
      case 'success':
        return 'border-emerald-200 bg-emerald-50 text-emerald-950';
      case 'info':
        return 'border-blue-200 bg-blue-50 text-blue-950';
      case 'warning':
        return 'border-amber-200 bg-amber-50 text-amber-950';
      case 'error':
        return 'border-red-200 bg-red-50 text-red-950';
      default:
        return '';
    }
  }

  public async onActionClick(event: MouseEvent): Promise<void> {
    if (this._isLoading() || !this.toast.action) return; // Prevent double-clicks

    this._isLoading.set(true);
    try {
      // Execute the action and wait for it to complete
      await Promise.resolve(this.toast.action.onClick(event));

      // If event wasn't prevented, dismiss the toast after action completes
      if (!event.defaultPrevented) {
        this.dismiss.emit(this.toast.id);
      } else {
        // Action prevented default, keep toast open but stop loading
        this._isLoading.set(false);
      }
    } catch (error) {
      clientLogger.error('Action failed', {
        error: error instanceof Error ? error : new Error(String(error)),
      });
      // Keep toast open on error so user can retry
      this._isLoading.set(false);
    }
  }

  public onCancelClick(event: MouseEvent): void {
    if (this._isLoading()) return; // Prevent clicks during action
    this.cancelClick.emit(event);
  }
}
